import { Page, Text, View, Document } from "@react-pdf/renderer";
import { parseDate } from "helpers/date";
import { useMemo } from "react";
import {
  IDefaultProcedure,
  IOrder,
  IPatientResponsibilityProcedure,
} from "types/Order/Order";
import { IProvider } from "types/Provider/Provider";

import { BreakDownSection } from "./BreakDownSection";
import { Footer } from "./Footer";
import { Message } from "./Message";
import { PatientVisitDetails } from "./PatientVisitDetails";
import { PaymentSummary } from "./PaymentSummary";
import { ServicesProvidedList } from "./ServicesProvidedList";

type UniqueProcedureList = IDefaultProcedure | IPatientResponsibilityProcedure;

export function ReceiptDefault({
  styles,
  logoUrl,
  orderInfo,
  uniqueProcedureList,
  paymentsInfo,
  showPaymentPlanInfo,
  paymentPlanInfo,
  paymentInfoFull,
  cardData,
  secondCardData,
  directPayNote,
  clearLogo,
  isVersion2,
  showPatientSignature,
  insuranceInfo,
  breakDownInfo,
  isCreatedWithDirectPay,
}: {
  styles: any;
  logoUrl: string;
  orderInfo: IOrder;
  uniqueProcedureList: UniqueProcedureList[];
  paymentsInfo: any;
  showPaymentPlanInfo: boolean;
  paymentPlanInfo: any;
  paymentInfoFull: any;
  cardData: any;
  secondCardData: any;
  directPayNote?: string | null;
  clearLogo: string;
  isVersion2?: boolean;
  showPatientSignature?: boolean;
  insuranceInfo?: string | null;
  breakDownInfo: any;
  isCreatedWithDirectPay: boolean;
}) {
  const mainFacility = useMemo(() => {
    const mainFacility = orderInfo.account.providers.find(
      (aProvider) => aProvider.mainFacility === true
    ) as IProvider;

    return mainFacility;
  }, [orderInfo]);

  return (
    <Document>
      <Page size="LETTER" style={styles.page} wrap>
        <Footer styles={styles} clearLogo={clearLogo} />
        <View style={styles.content}>
          <Text
            style={{
              ...styles.pageDescription,
              fontSize: "15px",
              marginBottom: "0px",
            }}
          >
            Payment Receipt
          </Text>
          <Text
            style={{
              ...styles.pageDescription,
              marginBottom: "0px",
              marginTop: "5px",
              fontSize: "12px",
            }}
          >
            {mainFacility.name}
          </Text>
          <Text
            style={{
              ...styles.pageDescription,
              fontSize: "12px",
              marginTop: "5px",
            }}
          >
            {mainFacility.streetAddress}, {mainFacility.city},{" "}
            {mainFacility.state}, {mainFacility.zipcode}
          </Text>
          <View style={styles.container}>
            <PatientVisitDetails
              styles={styles}
              firstName={orderInfo?.patient.firstName}
              lastName={orderInfo?.patient.lastName}
              dateOfService={parseDate(orderInfo?.dateOfService)}
              accountNumber={orderInfo?.accountNumber}
              insuranceInfo={insuranceInfo}
              isCreatedWithDirectPay={isCreatedWithDirectPay}
            />
            <ServicesProvidedList
              styles={styles}
              uniqueProcedureList={uniqueProcedureList}
            />
          </View>
          <View style={styles.container} wrap>
            <PaymentSummary
              styles={styles}
              paymentsInfo={paymentsInfo}
              showPaymentPlanInfo={showPaymentPlanInfo}
              paymentPlanInfo={paymentPlanInfo}
              paymentInfoFull={paymentInfoFull}
              cardData={cardData}
              secondCardData={secondCardData}
              directPayNote={directPayNote}
            />
          </View>
          {isVersion2 && breakDownInfo?.length && (
            <>
              <View style={styles.container}>
                <BreakDownSection
                  styles={styles}
                  breakDownInfo={breakDownInfo}
                  showLastPaymentAmount={!!orderInfo?.paymentPlanBreakdown}
                />
              </View>
              {showPaymentPlanInfo && (
                <View
                  style={{
                    ...styles.container,
                    gap: "0px",
                    margin: "0px 20px",
                  }}
                  wrap={false}
                  minPresenceAhead={50}
                >
                  <Message styles={styles} />
                </View>
              )}
            </>
          )}
        </View>
        {showPatientSignature && (
          <View
            style={{
              marginTop: 40,
              marginBottom: 20,
              borderTop: "1px solid black",
              width: "50%",
              alignSelf: "center",
            }}
            wrap={false}
          >
            <Text
              style={{
                ...styles.text,
                textAlign: "center",
                marginTop: 4,
              }}
            >
              Sign here
            </Text>
          </View>
        )}
      </Page>
    </Document>
  );
}
